import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ActionIcon,
  Button,
  Group,
  Skeleton,
  Switch,
  Tabs,
  Text,
  Textarea,
  Tooltip,
  Title,
} from "@mantine/core";
import { DateTimePicker } from "@mantine/dates";
import { useMediaQuery } from "@mantine/hooks";
import {
  IconCalendarTime,
  IconFileDescription,
  IconPencil,
  IconPlus,
  IconShieldCheck,
  IconTrash,
} from "@tabler/icons-react";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import MGAAPI from "../../../api/index";
import { getErrorMessage } from "../../../utils/errorUtils";
import SEConfirmModal from "../../../components/common/SEConfirmModal";
import TicketNotification from "../../globalParameters/TicketNotification";
import { useStyles } from "./cardStyles";
import "./style.scss";

const apiSession = new MGAAPI();

const DEFAULT_FORM_STATE = {
  startDateTime: null,
  endDateTime: null,
  description: "",
  enabled: true,
};

const toValidDate = (value) => {
  if (!value) return null;
  const dateValue = value instanceof Date ? value : new Date(value);
  return Number.isNaN(dateValue.getTime()) ? null : dateValue;
};

const normalizeMaintenance = (rawResponse) => {
  const payload = rawResponse?.data ?? rawResponse;

  if (Array.isArray(payload)) {
    return payload[0] || null;
  }

  if (Array.isArray(payload?.content)) {
    return payload.content[0] || null;
  }

  if (payload && typeof payload === "object") {
    const hasMaintenanceData =
      payload.startDateTime ||
      payload.endDateTime ||
      payload.description ||
      typeof payload.enabled === "boolean";

    return hasMaintenanceData ? payload : null;
  }

  return null;
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const parsed = dayjs(value);
  if (!parsed.isValid()) return "-";
  return parsed.format("DD MMM YYYY, hh:mm A");
};

const MaintenancePage = () => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const isMobile = useMediaQuery("(max-width: 992px)");
  const [activeTab, setActiveTab] = useState("maintenance");
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isToggling, setIsToggling] = useState(false);
  const [maintenance, setMaintenance] = useState(null);
  const [pageError, setPageError] = useState("");
  const [formMode, setFormMode] = useState("add");
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [formValues, setFormValues] = useState(DEFAULT_FORM_STATE);
  const [errors, setErrors] = useState({});

  const resetForm = () => {
    setFormValues(DEFAULT_FORM_STATE);
    setErrors({});
  };

  const loadMaintenance = useCallback(async () => {
    setLoading(true);
    setPageError("");
    try {
      const response = await apiSession.getMaintenance();
      const normalized = normalizeMaintenance(response);
      setMaintenance(normalized);
    } catch (error) {
      if (error?.response?.status === 404) {
        setMaintenance(null);
      } else {
        setPageError(getErrorMessage(error));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === "maintenance") {
      loadMaintenance();
    }
  }, [activeTab, loadMaintenance]);

  const openAddModal = () => {
    setFormMode("add");
    resetForm();
    setIsFormOpen(true);
  };

  const openEditModal = () => {
    setFormMode("edit");
    setErrors({});
    setFormValues({
      startDateTime: toValidDate(maintenance?.startDateTime),
      endDateTime: toValidDate(maintenance?.endDateTime),
      description: maintenance?.description || "",
      enabled: maintenance?.enabled !== false,
    });
    setIsFormOpen(true);
  };

  const closeModal = () => {
    setIsFormOpen(false);
    setErrors({});
  };

  const validateForm = () => {
    const nextErrors = {};
    const cleanDescription = String(formValues.description || "").trim();

    if (!formValues.startDateTime) {
      nextErrors.startDateTime = t("maintenance.validation.startRequired");
    } else if (dayjs(formValues.startDateTime).isBefore(dayjs())) {
      nextErrors.startDateTime = t("maintenance.validation.startInFuture");
    }

    if (!formValues.endDateTime) {
      nextErrors.endDateTime = t("maintenance.validation.endRequired");
    } else if (dayjs(formValues.endDateTime).isBefore(dayjs())) {
      nextErrors.endDateTime = t("maintenance.validation.endInFuture");
    }

    if (
      formValues.startDateTime &&
      formValues.endDateTime &&
      !nextErrors.endDateTime &&
      !dayjs(formValues.endDateTime).isAfter(dayjs(formValues.startDateTime))
    ) {
      nextErrors.endDateTime = t("maintenance.validation.endAfterStart");
    }

    if (!cleanDescription) {
      nextErrors.description = t("maintenance.validation.descriptionRequired");
    } else if (cleanDescription.length > 500) {
      nextErrors.description = t("maintenance.validation.descriptionMaxLength");
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const getPayloadFromForm = useCallback(() => {
    const trimmedDescription = String(formValues.description || "").trim();
    return {
      title: maintenance?.title || "Maintenance",
      startDateTime: dayjs(formValues.startDateTime).toISOString(),
      endDateTime: dayjs(formValues.endDateTime).toISOString(),
      description: trimmedDescription,
      enabled: Boolean(formValues.enabled),
    };
  }, [formValues, maintenance]);

  const submitForm = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    setPageError("");

    const payload = getPayloadFromForm();

    try {
      if (formMode === "edit") {
        await apiSession.updateMaintenance(payload);
      } else {
        await apiSession.saveMaintenance(payload);
      }
      setIsFormOpen(false);
      await loadMaintenance();
    } catch (error) {
      setPageError(getErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const onDeleteMaintenance = async () => {
    setIsSubmitting(true);
    setPageError("");
    try {
      await apiSession.deleteMaintenance();
      setMaintenance(null);
      setIsDeleteConfirmOpen(false);
      resetForm();
    } catch (error) {
      setPageError(getErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const onToggleEnabled = async (nextEnabledState) => {
    if (!maintenance) return;

    const previous = maintenance;
    const next = { ...maintenance, enabled: nextEnabledState };
    setMaintenance(next);
    setIsToggling(true);

    try {
      await apiSession.updateMaintenance({
        title: maintenance?.title || "Maintenance",
        startDateTime: maintenance?.startDateTime,
        endDateTime: maintenance?.endDateTime,
        description: maintenance?.description,
        enabled: nextEnabledState,
      });
    } catch (error) {
      setMaintenance(previous);
      setPageError(getErrorMessage(error));
    } finally {
      setIsToggling(false);
    }
  };

  const descriptionCount = useMemo(
    () => String(formValues.description || "").length,
    [formValues.description],
  );

  return (
    <div className="maintenance-page">
      <Tabs
        value={activeTab}
        onTabChange={(value) => setActiveTab(value || "maintenance")}
        color="seGreen.0"
        orientation={isMobile ? "horizontal" : "vertical"}
        className="maintenance-tabs"
      >
        <Tabs.List className="maintenance-tabs__list">
          <Tabs.Tab value="maintenance" className="maintenance-tabs__tab">
            {t("maintenance.tabs.maintenance")}
          </Tabs.Tab>
          <Tabs.Tab value="ticketNotification" className="maintenance-tabs__tab">
            {t("maintenance.tabs.ticketNotification")}
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="maintenance" className="maintenance-tabs__panel">
          <div className="maintenance-header">
            <div>
              <Title order={2} className="maintenance-header__title">
                {t("maintenance.title")}
              </Title>
              <Text className="maintenance-header__subtitle">
                {t("maintenance.subtitle")}
              </Text>
            </div>
          </div>

          <div className={classes.card}>
            <div className="maintenance-table-shell">
              <div>
                <div className={classes.header}>
                  <Text className={classes.title}>{t("maintenance.scheduledMaintenance")}</Text>
                  <Group spacing="sm" className={classes.rightSection}>
                    {!loading && !maintenance && (
                      <Button
                        leftIcon={<IconPlus size={16} />}
                        color="seGreen.0"
                        onClick={openAddModal}
                      >
                        {t("maintenance.addMaintenance")}
                      </Button>
                    )}
                    {!loading && maintenance && (
                      <>
                        <Text size="sm" c="dimmed">
                          {t("maintenance.enabled")}
                        </Text>
                        <Switch
                          checked={maintenance.enabled !== false}
                          color="seGreen.0"
                          onChange={(event) => onToggleEnabled(event.currentTarget.checked)}
                          aria-label="toggle-maintenance"
                        />
                        <div className={classes.divider} />
                        <Tooltip label={t("maintenance.editTooltip")} withArrow>
                          <ActionIcon
                            variant="subtle"
                            radius="md"
                            size={42}
                            className={classes.editButton}
                            onClick={openEditModal}
                            aria-label="edit-maintenance"
                          >
                            <IconPencil size={28} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label={t("maintenance.deleteTooltip")} withArrow>
                          <ActionIcon
                            variant="subtle"
                            radius="md"
                            size={42}
                            className={classes.deleteButton}
                            onClick={() => setIsDeleteConfirmOpen(true)}
                            aria-label="delete-maintenance"
                          >
                            <IconTrash size={28} />
                          </ActionIcon>
                        </Tooltip>
                      </>
                    )}
                  </Group>
                </div>

                {(loading || isToggling) && (
                  <>
                    {[1, 2, 3, 4].map((rowKey) => (
                      <div className={classes.row} key={rowKey}>
                        <Skeleton className={classes.iconContainer} radius="md" />
                        <div className={classes.content}>
                          <Skeleton height={12} width="35%" radius="sm" />
                          <Skeleton height={16} width="60%" radius="sm" />
                        </div>
                      </div>
                    ))}
                  </>
                )}

                {!loading && !isToggling && !maintenance && (
                  <div className="maintenance-layout-body">
                    <div className="maintenance-empty-state">
                      <Text className="maintenance-state-text">
                        {t("maintenance.emptyState")}
                      </Text>
                    </div>
                  </div>
                )}

                {!loading && !isToggling && maintenance && (
                  <>
                    <div className={classes.row}>
                      <div className={classes.iconContainer} aria-hidden="true">
                        <IconCalendarTime size={22} />
                      </div>
                      <div className={classes.content}>
                        <Text size="xs" className={classes.label}>
                          {t("maintenance.fromDateTime")}
                        </Text>
                        <Text size="sm" className={classes.value}>
                          {formatDateTime(maintenance.startDateTime)}
                        </Text>
                      </div>
                    </div>
                    <div className={classes.row}>
                      <div className={classes.iconContainer} aria-hidden="true">
                        <IconCalendarTime size={22} />
                      </div>
                      <div className={classes.content}>
                        <Text size="xs" className={classes.label}>
                          {t("maintenance.toDateTime")}
                        </Text>
                        <Text size="sm" className={classes.value}>
                          {formatDateTime(maintenance.endDateTime)}
                        </Text>
                      </div>
                    </div>
                    <div className={classes.row}>
                      <div className={classes.iconContainer} aria-hidden="true">
                        <IconFileDescription size={22} />
                      </div>
                      <div className={classes.content}>
                        <Text size="xs" className={classes.label}>
                          {t("maintenance.description")}
                        </Text>
                        <Text size="sm" className={`${classes.value} maintenance-description-cell`}>
                          {maintenance.description || "-"}
                        </Text>
                      </div>
                    </div>
                    <div className={classes.row}>
                      <div className={classes.iconContainer} aria-hidden="true">
                        <IconShieldCheck size={22} />
                      </div>
                      <div className={classes.content}>
                        <Text size="xs" className={classes.label}>
                          {t("maintenance.status")}
                        </Text>
                        <Group spacing={8} className={classes.status}>
                          <span
                            className={
                              maintenance.enabled !== false
                                ? classes.dot
                                : classes.dotDisabled
                            }
                          />
                          <Text size="sm" className={classes.value}>
                            {maintenance.enabled !== false ? t("maintenance.enabled") : t("maintenance.disabled")}
                          </Text>
                        </Group>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {pageError && (
            <Text className="maintenance-error" role="alert">
              {pageError}
            </Text>
          )}
        </Tabs.Panel>

        <Tabs.Panel value="ticketNotification" className="maintenance-tabs__panel">
          <TicketNotification />
        </Tabs.Panel>

      </Tabs>

      <SEConfirmModal
        opened={isFormOpen}
        onClose={closeModal}
        onConfirm={submitForm}
        header={formMode === "edit" ? t("maintenance.editHeader") : t("maintenance.addMaintenance")}
        confirmLabel={formMode === "edit" ? t("maintenance.update") : t("maintenance.save")}
        cancelLabel={t("maintenance.cancel")}
        confirmLoading={isSubmitting}
        message={
          <>
            <DateTimePicker
              label={t("maintenance.fromDateTime")}
              withAsterisk
              value={formValues.startDateTime}
              onChange={(value) => {
                setFormValues((prev) => ({ ...prev, startDateTime: value }));
                setErrors((prev) => ({ ...prev, startDateTime: "", endDateTime: "" }));
              }}
              valueFormat="DD MMM YYYY, hh:mm A"
              error={errors.startDateTime}
              minDate={new Date()}
              className="maintenance-field"
            />

            <DateTimePicker
              label={t("maintenance.toDateTime")}
              withAsterisk
              value={formValues.endDateTime}
              onChange={(value) => {
                setFormValues((prev) => ({ ...prev, endDateTime: value }));
                setErrors((prev) => ({ ...prev, endDateTime: "" }));
              }}
              valueFormat="DD MMM YYYY, hh:mm A"
              error={errors.endDateTime}
              minDate={formValues.startDateTime || new Date()}
              className="maintenance-field"
            />

            <Textarea
              label={t("maintenance.description")}
              withAsterisk
              value={formValues.description}
              onChange={(event) => {
                const nextValue = event.currentTarget.value;
                if (nextValue.length <= 500) {
                  setFormValues((prev) => ({ ...prev, description: nextValue }));
                }
                setErrors((prev) => ({ ...prev, description: "" }));
              }}
              minRows={4}
              maxLength={500}
              error={errors.description}
              className="maintenance-field"
            />

            <Text className="maintenance-char-counter">{descriptionCount}/500</Text>

            <Switch
              label={t("maintenance.enableMaintenance")}
              checked={formValues.enabled}
              onChange={(event) => {
                const isChecked = event.currentTarget.checked;
                setFormValues((prev) => ({
                  ...prev,
                  enabled: isChecked,
                }));
              }}
              color="seGreen.0"
              className="maintenance-field"
            />
          </>
        }
      />

      <SEConfirmModal
        opened={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        onConfirm={onDeleteMaintenance}
        header={t("maintenance.deleteHeader")}
        message={t("maintenance.deleteConfirmMessage")}
        confirmLabel={t("maintenance.delete")}
        cancelLabel={t("maintenance.cancel")}
        confirmLoading={isSubmitting}
      />
    </div>
  );
};

export default MaintenancePage;
